/// <amd-module name="Test/TestPlansHubRefresh/TestCaseWorkItemControls/TestSteps/TestStepsUtils/ParameterCommonUtils" />

// Based on code in \Tfs\Service\WebAccess\TestManagement\Scripts\TFS.TestManagement.ts
import { HtmlUtils } from "Test/TestPlansHubRefresh/TestCaseWorkItemControls/TestSteps/TestStepsUtils/HtmlUtils";
import * as UCD from "Test/TestPlansHubRefresh/TestCaseWorkItemControls/TestSteps/TestStepsUtils/UnicodeGeneralCategoryData";
import { ArrayUtils as Utils_Array } from "Test/TestPlansHubRefresh/TestCaseWorkItemControls/TestSteps/TestStepsUtils/ArrayUtils";
import * as TFS_Core_Utils from "Wit/Common/Dictionary";
import * as Utils_String from "VSS/Core/Util/String";

export module ParameterConstants {
    export let nameCharacters =
        "[" +
        UCD.unicodeCategory.Nd +
        UCD.unicodeCategory.Mn +
        UCD.unicodeCategory.Mc +
        UCD.unicodeCategory.Ll +
        UCD.unicodeCategory.Lo +
        UCD.unicodeCategory.Lu +
        UCD.unicodeCategory.Lt +
        "_-]";
    export let letterCharacters = "[" + UCD.unicodeCategory.Ll + UCD.unicodeCategory.Lo + UCD.unicodeCategory.Lu + UCD.unicodeCategory.Lt + "]";
    export let parameterCharacters =
        "[" +
        UCD.unicodeCategory.Lu +
        UCD.unicodeCategory.Ll +
        UCD.unicodeCategory.Lt +
        UCD.unicodeCategory.Lm +
        UCD.unicodeCategory.Lo +
        UCD.unicodeCategory.Nl +
        UCD.unicodeCategory.Nd +
        UCD.unicodeCategory.Mn +
        UCD.unicodeCategory.Pd +
        UCD.unicodeCategory.Pc +
        UCD.unicodeCategory.Mc +
        UCD.unicodeCategory.Cf +
        "_@]";
    export let nonParameterCharacters =
        "[^" +
        UCD.unicodeCategory.Lu +
        UCD.unicodeCategory.Ll +
        UCD.unicodeCategory.Lt +
        UCD.unicodeCategory.Lm +
        UCD.unicodeCategory.Lo +
        UCD.unicodeCategory.Nl +
        UCD.unicodeCategory.Nd +
        UCD.unicodeCategory.Mn +
        UCD.unicodeCategory.Pd +
        UCD.unicodeCategory.Pc +
        UCD.unicodeCategory.Mc +
        UCD.unicodeCategory.Cf +
        "_@]";
    // RegEx for parameter: 1) First char "@" followed by an optional "?" followed by nonwhitespace and non-punc characters
    // OR 2) Whitespace or punctuation characters followed by "@" followed by an optional "?" followed by  nonwhitespace and non-punc characters
    export let paramRegEx = new RegExp(
        "(^@[?]?" + parameterCharacters + "{1,})|(" + nonParameterCharacters + "{1,}@[?]?" + parameterCharacters + "{1,})"
    );
    export let encodeCharPattern = /_[Xx]([0-9a-fA-F]{4})_/;
    export let xml4eLetterPattern = "[" + UCD.unicodeCategory.LetterXml4e + "]";
    export let xml4eNameCharacterPattern = "[" + UCD.unicodeCategory.NcNameXml4e + "]";
}

export class ParameterCommonUtils {
    public static getParameters(parameterizedString: string): string[] {
        /// <summary>Parses the string to find parameters and returns the list of parameters</summary>
        /// <param name="parameterizedString" type="string">The string which is to be parsed</param>
        let currentParameter,
            found = true,
            arr,
            lines = HtmlUtils.getPlainTextLines(parameterizedString),
            res = [];

        for (var i = 0; i < lines.length; i++) {
            var parsedString = lines[i];
            while ((arr = ParameterConstants.paramRegEx.exec(parsedString)) !== null) {
                currentParameter = arr[0].substr(arr[0].indexOf("@") + 1);
                // Remove the option "?" from the starting of the param name
                if (currentParameter[0] === "?") {
                    currentParameter = currentParameter.substr(1);
                }
                if (Utils_Array.indexOf(res, currentParameter) === -1) {
                    res.push(currentParameter);
                }
                parsedString = parsedString.substr(arr.index + arr[0].length);
            }
        }

        return res;
    }

    public static convertCharToHex(char: string): string {
        let hexValue = char.charCodeAt(0).toString(16).toUpperCase();

        while (hexValue.length < 4) {
            hexValue = "0" + hexValue;
        }
        hexValue = "_x" + hexValue + "_";

        return hexValue;
    }

    public static updateEncodedParameters(parameterNames: string[]): TFS_Core_Utils.Dictionary<string> {
        let i = 0,
            paramCount: number = parameterNames.length,
            paramName: string,
            encodedParams: TFS_Core_Utils.Dictionary<string> = {};

        for (i = 0; i < paramCount; i++) {
            paramName = parameterNames[i];
            if (paramName && !encodedParams.hasOwnProperty(paramName)) {
                encodedParams[paramName] = ParameterCommonUtils.encodeName(paramName);
            }
        }

        return encodedParams;
    }

    public static encodeName(paramName: string) {
        let result = "",
            length = paramName.length,
            startIndex: number,
            specialCharIndex = 0,
            encodeCharPattern = ParameterConstants.encodeCharPattern,
            matches,
            charPatternIndex = -1,
            matchResult,
            i = 0,
            matchCount = 0,
            indexInParamName = 0,
            charPatternIndexList: number[] = [],
            matchedString: string;

        if (!paramName || paramName === "") {
            return paramName;
        }
        startIndex = 0;
        matchedString = paramName;
        if (paramName.indexOf("_") >= 0) {
            //Store the indexes for all matches for patterns of type encodeCharPattern
            while ((matchResult = encodeCharPattern.exec(matchedString)) != null) {
                charPatternIndexList[i++] = matchResult.index + indexInParamName;
                indexInParamName = indexInParamName + matchResult.index + matchResult[0].length - 1;
                matchedString = matchedString.substr(matchResult.index + matchResult[0].length - 1);
            }
            matches = encodeCharPattern.exec(paramName);
        }
        if (matches != null) {
            charPatternIndex = charPatternIndexList[matchCount++];
            matchedString = paramName.substr(matches.index + matches[0].length - 1);
        }
        if ((!ParameterCommonUtils.IsLetterOrUnderscore(paramName[0]) && paramName[0] !== ":") || charPatternIndex === 0) {
            result += ParameterCommonUtils.convertCharToHex(paramName[0]);
            startIndex = 1;
            specialCharIndex++;
            if (charPatternIndex === 0) {
                matches = encodeCharPattern.exec(matchedString);
                if (matches) {
                    charPatternIndex = charPatternIndexList[matchCount++];
                    matchedString = matchedString.substr(matches.index + matches[0].length - 1);
                }
            }
        }
        while (specialCharIndex < length) {
            if (!ParameterCommonUtils.IsNameCharacterOrColon(paramName[specialCharIndex]) || charPatternIndex === specialCharIndex) {
                if (charPatternIndex === specialCharIndex) {
                    matches = encodeCharPattern.exec(matchedString);
                    if (matches) {
                        charPatternIndex = charPatternIndexList[matchCount++];
                        matchedString = matchedString.substr(matches.index + matches[0].length - 1);
                    }
                }

                result = result + paramName.substr(startIndex, specialCharIndex - startIndex);
                result = result + ParameterCommonUtils.convertCharToHex(paramName[specialCharIndex]);
                startIndex = specialCharIndex + 1;
            }
            specialCharIndex++;
        }

        if (startIndex === 0) {
            return paramName;
        }
        if (startIndex < length) {
            result = result + paramName.substr(startIndex, length - startIndex);
        }
        return result;
    }

    public static decodeName(paramName: string): string {
        let result = "",
            length = paramName.length,
            matchResult,
            i = 0,
            indexInParamName = 0,
            matches,
            startIndex = 0,
            combinedChar: number,
            c_encodedCharLength = 7,
            decodeCharPattern = ParameterConstants.encodeCharPattern,
            charPatternIndex = -1,
            charPatternIndexList: number[] = [],
            matchedString: string,
            matchCount = 0,
            index: number;

        if (!paramName || paramName === "") {
            return paramName;
        }

        index = paramName.indexOf("_");
        if (index < 0) {
            return paramName;
        }
        matchedString = paramName;
        //Store the indexes for all matches for patterns of type encodeCharPattern
        while ((matchResult = decodeCharPattern.exec(matchedString)) != null) {
            charPatternIndexList[i++] = matchResult.index + indexInParamName;
            indexInParamName = indexInParamName + matchResult.index + matchResult[0].length;
            matchedString = matchedString.substr(matchResult.index + matchResult[0].length);
        }
        if (charPatternIndexList[matchCount] !== undefined) {
            charPatternIndex = charPatternIndexList[matchCount++];
        }
        for (i = 0; i < length - c_encodedCharLength + 1; i++) {
            if (i === charPatternIndex) {
                if (charPatternIndexList[matchCount] !== undefined) {
                    charPatternIndex = charPatternIndexList[matchCount++];
                }
                result = result + paramName.substr(startIndex, i - startIndex);
                if (paramName[i + c_encodedCharLength - 1] !== "_") {
                    combinedChar = this.getCombinedCharCodeForSurrogatePattern(paramName, i);
                    if (combinedChar < parseInt("10000", 16)) {
                        startIndex = i + c_encodedCharLength + 4;
                        result = result + String.fromCharCode(combinedChar);
                    }
                    i += c_encodedCharLength - 1 + 4;
                } else {
                    startIndex = i + c_encodedCharLength;
                    combinedChar =
                        parseInt(paramName[i + 2], 16) * 4096 +
                        parseInt(paramName[i + 3], 16) * 256 +
                        parseInt(paramName[i + 4], 16) * 16 +
                        parseInt(paramName[i + 5], 16);
                    result = result + String.fromCharCode(combinedChar);
                    i += c_encodedCharLength - 1;
                }
            }
        }
        if (startIndex === 0) {
            return paramName;
        }
        if (startIndex < length) {
            result = result + paramName.substr(startIndex, length - startIndex);
        }
        return result;
    }

    private static getCombinedCharCodeForSurrogatePattern(paramName: string, index: number) {
        return (
            parseInt(paramName[index + 2], 16) * 268435456 +
            parseInt(paramName[index + 3], 16) * 16777216 +
            parseInt(paramName[index + 4], 16) * 1048576 +
            parseInt(paramName[index + 5], 16) * 65536 +
            parseInt(paramName[index + 6], 16) * 4096 +
            parseInt(paramName[index + 7], 16) * 256 +
            parseInt(paramName[index + 8], 16) * 16 +
            parseInt(paramName[index + 9], 16)
        );
    }

    public static isLetter(character: string) {
        let paramRegEx = new RegExp("(" + ParameterConstants.xml4eLetterPattern + "{1})"),
            result = paramRegEx.exec(character);
        if (result === null) {
            return false;
        }
        return true;
    }

    public static IsLetterOrUnderscore(char: string) {
        if (!ParameterCommonUtils.isLetter(char)) {
            return char === "_";
        }
        return true;
    }

    public static IsNameCharacterOrColon(char: string) {
        if (!ParameterCommonUtils.isNameCharacter(char)) {
            return char === ":";
        }
        return true;
    }

    public static isNameCharacter(character: string) {
        let paramRegEx = new RegExp("(" + ParameterConstants.xml4eNameCharacterPattern + "{1})"),
            result = paramRegEx.exec(character);
        if (result === null) {
            return false;
        }
        return true;
    }

    public static hasParameters(content: string): boolean {
        let dataWithoutAhref, paramName, paramRegEx;

        if (content) {
            // We do not need to search the parameters in the attributes of the Anchor tags. In case of the anchor tags we need to check only the display text of the link.
            // So removing the starting anchor tag from the string so that we dont get a false parameter (some text in anchor tag href attribute starting with @)
            // Regular expression used here: <a > tag with anything after a, but used ? for the shortest match.
            dataWithoutAhref = content.replace(/<a((\s|\S)*?)>/i, "");

            paramName = ParameterConstants.paramRegEx.exec(dataWithoutAhref);
            if (paramName === null) {
                //Try with plain text also
                paramName = ParameterConstants.paramRegEx.exec(HtmlUtils.sanitizedText(content));
                if (paramName === null) {
                    return false;
                }
            }

            return true;
        }
        return false;
    }

    public static isValidParameterString(paramName: string): boolean {
        let paramRegEx = new RegExp("(" + ParameterConstants.parameterCharacters + "{1,})"),
            result = paramRegEx.exec(paramName);
        if (result === null || result[0] !== paramName) {
            return false;
        }
        return true;
    }

    public static createParamRegExForString(paramName: string) {
        return new RegExp("(^@[?]?" + paramName + ")|(" + ParameterConstants.nonParameterCharacters + "{1}@[?]?" + paramName + ")", "ig");
    }

    public static getParametersDataRow($parametersXml: Element) {
        let that = this,
            paramName: string,
            paramValue,
            params = [],
            parameterDataRow: { [id: string]: string } = {};

        //loop through each param and only add ones that are not already in the list
        var $parameters = $parametersXml.querySelectorAll("parameters");
        for (var i = 0; i < $parameters.length; i++) {
            var $parameterChildren = $parameters[i].children;
            for (var j = 0; j < $parameterChildren.length; j++) {
                var $parameterChild = $parameterChildren[j];
                paramName = $parameterChild.getAttribute("name") ?? "";
                paramValue = $parameterChild.querySelector("value")?.textContent ?? "";
                if (Utils_Array.indexOf(params, paramName) === -1) {
                    params.push(paramName);
                    parameterDataRow[paramName] = paramValue;
                }
            }
        }
        return parameterDataRow;
    }

    public static parseParameters($parameters: Element): string[] {
        let paramName,
            params: string[] = [];

        //loop through each param and only add ones that are not already in the list
        var $parameterElements = $parameters.querySelectorAll("parameters");
        for (var i = 0; i < $parameterElements.length; i++) {
            var $parameterChildren = $parameterElements[i].children;
            for (var j = 0; j < $parameterChildren.length; j++) {
                var $parameterChild = $parameterChildren[j];
                paramName = $parameterChild.getAttribute("name") ?? "";
                if (Utils_Array.indexOf(params, paramName) === -1) {
                    params.push(paramName);
                }
            }
        }
        return params;
    }

    public static getParameterCountInString(targetString: string, paramName: string): number {
        /// <summary>Parses the string to find the count of parameters with the name paramName</summary>

        let currentParameter: string,
            found = true,
            arr,
            lines = HtmlUtils.getPlainTextLines(targetString),
            paramCount = 0;

        for (var i = 0; i < lines.length; i++) {
            var parsedString = lines[i];
            while ((arr = ParameterConstants.paramRegEx.exec(parsedString)) !== null) {
                currentParameter = arr[0].substr(arr[0].indexOf("@") + 1);
                // Remove the optional "?" from the starting of the param name
                if (currentParameter[0] === "?") {
                    currentParameter = currentParameter.substr(1);
                }
                if (Utils_String.localeIgnoreCaseComparer(currentParameter, paramName) === 0) {
                    paramCount++;
                }
                parsedString = parsedString.substr(arr.index + arr[0].length);
            }
        }

        return paramCount;
    }

    public static renameParameterInPlainString(targetString: string, paramName: string, newParamName: string): string {
        /// <summary>Renames parameter in plain text</summary>

        let result = "",
            lines = HtmlUtils.getPlainTextLines(targetString);

        for (var i = 0; i < lines.length; i++) {
            var parsedString = lines[i];
            parsedString = this.renameParameterInString(parsedString, paramName, newParamName);

            if (parsedString !== "") {
                result = result + "<p>" + parsedString + "</p>";
            }
        }

        return result;
    }

    public static renameParameterInString(targetString: string, paramName: string, newParamName: string): string {
        // If the newParamName is empty, then the parameter "paramName" will be removed.
        let arr,
            result,
            paramsRenamed = 0,
            count = this.getParameterCountInString(targetString, paramName),
            paramRegEx: RegExp = ParameterCommonUtils.createParamRegExForString(paramName),
            originalString = targetString,
            matchLength: number,
            matchIndex: number;

        if (paramName === newParamName) {
            return targetString;
        }

        while ((arr = paramRegEx.exec(targetString)) !== null) {
            matchLength = arr[0].length;
            matchIndex = arr.index;
            // If the string starts with @, the regEx match would start from '@' else it would start from the non-param character before '@'
            if (targetString.substring(arr.index, 1) === "@") {
                matchIndex = matchIndex - 1;
                matchLength = matchLength + 1;
            }
            if (this._isNonParamCharacter(targetString, matchIndex + matchLength)) {
                if (newParamName && newParamName !== "") {
                    targetString = targetString.substring(0, matchIndex + 2) + newParamName + targetString.substring(matchIndex + matchLength);
                    paramRegEx.lastIndex = matchIndex + 2 + newParamName.length;
                } else {
                    //Remove the parameter
                    // Remove all the "@"s in the beginning of the parameter so that the remaining string is not a parameter.
                    result = /^@{1,}/.exec(targetString.substring(matchIndex + 1));
                    if (result) {
                        targetString = targetString.substring(0, matchIndex + 1) + targetString.substring(matchIndex + 1 + result[0].length);
                        paramRegEx.lastIndex = matchIndex + 1 + result[0].length;
                    }
                }
                paramsRenamed++;
            } else {
                paramRegEx.lastIndex = matchIndex + matchLength;
            }
        }
        if (paramsRenamed === count && this.getParameterCountInString(targetString, paramName) === 0) {
            return targetString;
        } else {
            // If all the parameters were not renamed in the rich text, try with plain text
            return ParameterCommonUtils.renameParameterInPlainString(originalString, paramName, newParamName);
        }
    }

    private static _isNonParamCharacter(targetString: string, index: number): boolean {
        if (index >= 0 && index < targetString.length) {
            if (targetString[index].match(ParameterConstants.nonParameterCharacters)) {
                return true;
            }
        } else if (index === targetString.length) {
            // In case the preceeding character was the last character we can return true as it is the end of the string and non-param char is not required to end the param name
            return true;
        }
        return false;
    }
}
